// @flow
import { useCallback, useEffect, useState, useMemo } from 'react';
import SECTION_NAMES, {
  SECTION_PARENT_NAMES,
  SectionNames,
} from 'constants/sectionNames';
import { useHistory } from 'react-router-dom';
import {
  determineSectionsProgressUpdate,
  getCompleteStep,
  getIncompleteStep,
  getPreviousStep,
  getRequiredSteps,
  getSectionNameFromPathname,
  getStepFromPathname,
  getStepURLs,
  getUpdatedSteps,
} from 'hooks/sectionsProgress/utils';
import { usePrevious } from '@fortress-technology-solutions/fortress-component-library/hooks';
import { SectionSteps, SectionCompletionStatus } from './types';
import useIsUnInvitedSecondaryUser from 'hooks/useIsUnInvitedSecondaryUser';
import useEnableSectionsProgressLogging from 'hooks/sectionsProgress/useEnableSectionsProgressLogging';
import { useHouseholdMembersContext } from 'contexts/HouseholdMembersContext';
import useBaseURL from 'hooks/useBaseURL';

type Props = {
  initialSteps: SectionSteps,
  sectionName: SectionNames,
};

const useSectionsProgressManager = ({
  deleteSectionsProgress,
  setSectionsProgress,
  sectionsProgress,
  isInitialized,
}: Props = {}): {
  currentSectionProgress: SectionCompletionStatus,
  handleCompleteStep: Function,
  handleUpdate: Function,
  currentURL: string,
  previousURL: string,
  nextURL: string,
} => {
  const history = useHistory();
  const {
    action,
    location: { pathname, search },
  } = history;

  const baseURL = useBaseURL();
  const [initialSteps, setInitialSteps] = useState<Props>(null);
  const { isSectionsProgressLoggingEnabled } =
    useEnableSectionsProgressLogging();
  const { isPrimaryUser: isPrimaryProspect } = useHouseholdMembersContext();
  const isUnInvitedSecondaryUser = useIsUnInvitedSecondaryUser();

  const fullPathname = `${pathname}${search}`;
  const sectionName = useMemo(
    () => getSectionNameFromPathname({ pathname, baseURL }),
    [pathname, baseURL],
  );

  const [requiredSteps, setRequiredSteps] = useState<{
    completed: SectionSteps,
    required: SectionSteps,
  }>({ completed: [], required: [] });

  const previousPathname = usePrevious(fullPathname);

  const { currentSectionProgress, previousURL, nextURL, currentURL } =
    useMemo(() => {
      const currentSection = sectionsProgress?.[sectionName];
      const steps = currentSection?.steps;

      if (!Boolean(steps?.length))
        return {
          currentSectionProgress: { steps: [] },
          previousURL: null,
          nextURL: null,
          currentURL: null,
        };

      return {
        currentSectionProgress: currentSection,
        ...getStepURLs({
          steps,
          sectionName,
          baseURL,
          isPrimaryUser: isPrimaryProspect,
          isUnInvitedSecondaryUser,
        }),
      };
    }, [
      baseURL,
      isPrimaryProspect,
      isUnInvitedSecondaryUser,
      sectionName,
      sectionsProgress,
    ]);

  const initializeSection = useCallback((initialSteps) => {
    setInitialSteps(initialSteps);
  }, []);

  const updateRequiredSteps = useCallback(
    (steps: SectionSteps) => {
      const updatedRequiredSteps = getRequiredSteps({
        currentSectionProgressSteps: steps,
        isSectionsProgressLoggingEnabled,
      });

      setRequiredSteps(updatedRequiredSteps);

      return updatedRequiredSteps;
    },
    [isSectionsProgressLoggingEnabled],
  );

  const handleUpdate = useCallback(
    (update) => {
      const sectionsProgressUpdate = determineSectionsProgressUpdate({
        currentSectionProgress,
        isPrimaryProspect,
        isUnInvitedSecondaryUser,
        sectionName,
        sectionsProgress,
        update,
      });

      if (isSectionsProgressLoggingEnabled) {
        console.log('Update Payload:', sectionsProgressUpdate);
      }

      setSectionsProgress(sectionsProgressUpdate);

      return sectionsProgressUpdate;
    },
    [
      currentSectionProgress,
      isPrimaryProspect,
      isSectionsProgressLoggingEnabled,
      isUnInvitedSecondaryUser,
      sectionName,
      sectionsProgress,
      setSectionsProgress,
    ],
  );

  const handleCompleteStep = useCallback(
    (stepName: string | string[], updatedSteps?: SectionSteps) => {
      const { update, stepsUpdate } = getCompleteStep({
        currentSteps: currentSectionProgress.steps,
        isSectionsProgressLoggingEnabled,
        stepName,
        updatedSteps,
      });

      let updatedRequiredSteps = null;
      let updatedSectionsProgress = null;

      if (update) {
        updatedSectionsProgress = handleUpdate(update);
      }
      if (stepsUpdate) {
        updatedRequiredSteps = updateRequiredSteps(stepsUpdate);
      }

      return {
        updatedRequiredSteps,
        updatedSteps: stepsUpdate,
        updatedSectionsProgress,
      };
    },
    [
      currentSectionProgress.steps,
      handleUpdate,
      isSectionsProgressLoggingEnabled,
      updateRequiredSteps,
    ],
  );

  const updateSteps = useCallback(
    (initialSteps) => {
      const { updatedSectionsProgress, updatedSteps } = getUpdatedSteps({
        currentSectionProgress,
        effectMode: false,
        initialSteps,
        isPrimaryProspect,
        isSectionsProgressLoggingEnabled,
        isUnInvitedSecondaryUser,
        requiredSteps,
        sectionName,
        sectionsProgress,
      });

      if (updatedSectionsProgress) setSectionsProgress(updatedSectionsProgress);

      if (updatedSteps) updateRequiredSteps(updatedSteps);

      return updatedSteps;
    },
    [
      currentSectionProgress,
      isPrimaryProspect,
      isSectionsProgressLoggingEnabled,
      isUnInvitedSecondaryUser,
      requiredSteps,
      sectionName,
      sectionsProgress,
      setSectionsProgress,
      updateRequiredSteps,
    ],
  );

  const handleIncompleteStep = useCallback(
    (stepName) => {
      const update = getIncompleteStep({
        currentSectionProgress,
        isSectionsProgressLoggingEnabled,
        requiredSteps,
        stepName,
      });
      updateRequiredSteps(update.steps);
      handleUpdate(update);
    },
    [
      currentSectionProgress,
      handleUpdate,
      isSectionsProgressLoggingEnabled,
      requiredSteps,
      updateRequiredSteps,
    ],
  );

  // initialize section steps
  useEffect(() => {
    const isParentSection = Boolean(SECTION_PARENT_NAMES[sectionName]?.length);

    if (
      Boolean(!initialSteps?.length) ||
      !sectionName ||
      !isInitialized ||
      isParentSection
    ) {
      return;
    }

    const { updatedSectionsProgress, updatedSteps } = getUpdatedSteps({
      currentSectionProgress,
      initialSteps,
      isPrimaryProspect,
      isSectionsProgressLoggingEnabled,
      isUnInvitedSecondaryUser,
      requiredSteps,
      sectionName,
      sectionsProgress,
      effectMode: true,
    });

    if (updatedSectionsProgress) {
      setSectionsProgress(updatedSectionsProgress);
    }

    if (updatedSteps) updateRequiredSteps(updatedSteps);
    // only run when initialSteps changes
  }, [initialSteps]); // eslint-disable-line react-hooks/exhaustive-deps

  // manage step completion when pathname changes
  useEffect(() => {
    const isParentSection = Boolean(SECTION_PARENT_NAMES[sectionName]?.length);

    if (
      !pathname ||
      !previousPathname ||
      fullPathname === previousPathname ||
      !sectionName ||
      isParentSection
    )
      return;

    if (isSectionsProgressLoggingEnabled) {
      console.group(`PATHNAME CHANGE: ${sectionName}`);
      console.log('History Action:', action);
      console.log('Current Pathname:', fullPathname);
      console.log('Previous Pathname:', previousPathname);
      console.log('Required Steps:', requiredSteps);
    }

    if (action === 'PUSH') {
      const completedStep = getStepFromPathname({
        requiredSteps: requiredSteps.required,
        pathname: previousPathname,
      });
      if (completedStep?.name) {
        handleCompleteStep(completedStep.name);
      }
    } else if (action === 'POP' || action === 'REPLACE') {
      const previousStep = getPreviousStep({
        requiredSteps: requiredSteps.required,
        pathname: fullPathname,
      });
      if (previousStep?.name) {
        handleIncompleteStep(previousStep.name);
      } else if (isSectionsProgressLoggingEnabled) {
        console.log('No matching previous step found for the current path:', {
          fullPathname,
          previousPathname,
          requiredSteps,
        });
      }
    }

    if (isSectionsProgressLoggingEnabled) {
      console.groupEnd();
      console.log('');
    }

    // only run when pathname changes
  }, [pathname, search]); // eslint-disable-line react-hooks/exhaustive-deps

  const allSectionsAreComplete = useMemo(() => {
    if (!isInitialized) return false;

    const {
      // primary user sections
      [SECTION_NAMES.APPLICATION]: applicationSectionProgress,
      [SECTION_NAMES.CREDIT_AND_CRIMINAL]: creditAndCriminalSectionProgress,
      [SECTION_NAMES.DOCUMENTATION]: documentationSectionProgress,
      // uninvited secondary user sections
      [SECTION_NAMES.IDENTIFYING_DETAILS]: identifyingDetailsProgress,
      [SECTION_NAMES.CREDIT_CRIMINAL_CHECK]: creditCriminalCheckProgress,
      [SECTION_NAMES.SELF_IDENTIFICATION]: selfIdentificationProgress,
      [SECTION_NAMES.APPLICATION_SUBMISSION]: applicationSubmissionProgress,
    } = sectionsProgress;

    if (isUnInvitedSecondaryUser) {
      return Boolean(
        identifyingDetailsProgress?.isComplete &&
          creditCriminalCheckProgress?.isComplete &&
          selfIdentificationProgress?.isComplete &&
          applicationSubmissionProgress?.isComplete,
      );
    } else {
      return Boolean(
        applicationSectionProgress?.isComplete &&
          creditAndCriminalSectionProgress?.isComplete &&
          documentationSectionProgress?.isComplete,
      );
    }
  }, [isInitialized, isUnInvitedSecondaryUser, sectionsProgress]);

  return {
    allSectionsAreComplete,
    currentSectionProgress,
    currentURL,
    deleteSectionsProgress,
    handleCompleteStep,
    handleUpdate,
    initializeSection,
    nextURL,
    previousURL,
    updateSteps,
  };
};

export default useSectionsProgressManager;
